// frontend/src/pages/Home.jsx
import React, { lazy, Suspense, useContext } from "react";
import { Link } from "react-router-dom";
import GuideWelcome from "../components/GuideWelcome";
import "./Home.css";
import { Helmet } from "react-helmet-async";
import { AIContext } from "../context/AIContext";

// Lazy-load AIWidget
const AIWidget = lazy(() => import("../components/AIWidget"));

const SITE_ORIGIN = "https://www.kirunatours.com";

export default function Home() {
  const { isAIOpen, setIsAIOpen } = useContext(AIContext);

  const title = "Northern Lights Tours Kiruna | Aurora Viewing & Arctic Adventures";
  const desc = "Experience Northern Lights tours in Kiruna with local guides. Aurora viewing, dog sledding & Arctic adventures. Book your Northern Lights experience today!";

  return (
    <main className="home-container">
      <Helmet>
        <title>{title}</title>
        <meta property="og:title" content={title} />
        <meta property="og:description" content="Northern Lights tours, dog sledding, reindeer sledding with local guides in Kiruna and Abisko." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={`${SITE_ORIGIN}/`} />
        <meta name="robots" content="index,follow" />
        <link rel="canonical" href={`${SITE_ORIGIN}/`} />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "TravelAgency",
            name: "Kiruna Tours",
            description: "Local tour company in Kiruna since 2005. Authentic Arctic experiences with expert guides.",
            url: SITE_ORIGIN,
            address: {
              "@type": "PostalAddress",
              streetAddress: "Poikkijärvivägen 55",
              addressLocality: "Kiruna",
              addressRegion: "Norrbotten",
              postalCode: "981 92",
              addressCountry: "SE",
            },
            openingHoursSpecification: {
              "@type": "OpeningHoursSpecification",
              dayOfWeek: [
                "Monday",
                "Tuesday",
                "Wednesday",
                "Thursday",
                "Friday",
                "Saturday",
                "Sunday",
              ],
              opens: "09:00",
              closes: "18:00",
            },
            telephone: "+46706866709",
            tourBookingPage: `${SITE_ORIGIN}/tours/`
          })}
        </script>
      </Helmet>

      <header className="home-header">
        <h1>Kiruna Tours – Northern Lights & Arctic Experiences in Kiruna</h1>
        <p className="subtitle">
          Northern Lights tours • Dog sledding • Snowmobile safaris • Reindeer experiences
        </p>
      </header>

      <GuideWelcome />

      <section className="cta-section">
        <h2>Discover Swedish Lapland's Best Arctic Experiences</h2>
        <div className="cta-content">
          <p>
            <strong>Local experts since 2005</strong>, we specialize in small-group tours that showcase the real Arctic. Our most popular experiences include:
          </p>
          <ul className="popular-tours">
            <li>
              <Link to="/tours/northern-lights-car-chase-kiruna/">Northern Lights tours</Link> in Kiruna
            </li>
            <li>
              <Link to="/tours/drive-your-own-dog-sled-team/">Dog sledding adventures</Link> through frozen forests
            </li>
            <li>
              <Link to="/tours/snowmobile-adventure-kiruna-2-hour-arctic-wilderness-tour/">Snowmobile expeditions</Link> to remote wilderness areas
            </li>
            <li>
              {/* Populära turen (NY) */}
              <Link to="/tours/reindeer-sledding-day-tour-in-kiruna/">Reindeer sledding day tour</Link> in Kiruna
            </li>
            <li>
              {/* Extern länk ska vara <a> och med https:// */}
              <a href="https://www.aurorarivercamp.com" target="_blank" rel="noopener noreferrer">
                Glass igloo stays
              </a>{" "}
              at Aurora River Camp
            </li>
          </ul>
          <div className="cta-buttons">
            <Link to="/tours/" className="cta-button primary">Browse All Tours</Link>
            <Link to="/tours/northern-lights-sami-inspired-bbq-kiruna/" className="cta-button secondary">
              Northern Lights Tour with Sami Inspired BBQ
            </Link>
          </div>
        </div>
      </section>

      <section className="destination-section">
            <h2>Explore Two Arctic Paradises</h2>
            <div className="destination-cards">
                  <div className="destination-card">
                        <h3>Kiruna Adventures</h3>
                        <ul>
                              <li>Gateway to Swedish Lapland</li>
                              <li>Historic Icehotel visits</li>
                              <li><Link to="/tours/moose-spotting-tour-from-kiruna-to-nikkaluokta/" className="text-link">Moose safaris in winter wilderness</Link></li>
                              <li>Traditional Sami culture experiences</li>
                              {/* Nya länkar till orphan tours */}
                              <li><Link to="/tours/daytime-snowshoe-hike-kiruna/" className="text-link">Snowshoe Hiking Tours</Link></li>
                              <li><Link to="/tours/arctic-snowshoe-moments-fire-cocoa-marshmallows/" className="text-link">Arctic Snowshoe Moments</Link></li>
                              <li><Link to="/tours/narvik-fjord-and-narvik-city-sightseeing/" className="text-link">Fjord Sightseeing</Link></li>
                        </ul>
                        <Link to="/tours/" className="text-link">Discover Kiruna tours →</Link>
                  </div>
                  <div className="destination-card">
                        <h3>Abisko Experiences</h3>
                        <ul>
                              <li>Europe's best Northern Lights viewing</li>
                              <li>Famous "Blue Hole" microclimate</li>
                              <li>Snowshoe hiking in national park</li>
                              <li>Aurora Sky Station access</li>
                              {/* Nya länkar till orphan tours */}
                              <li><Link to="/tours/tour-to-narvik-from-abisko/" className="text-link">Tour to Narvik from Abisko</Link></li>
                              <li><Link to="/tours/morning-hike-in-abisko-national-park/" className="text-link">Morning Hikes</Link></li>
                        </ul>
                        <Link to="/tours/" className="text-link">Explore Abisko tours →</Link>
                  </div>
            </div>
      </section>

      <section className="benefits-section">
        <h2>Why Choose Kiruna Tours?</h2>
        <div className="benefits-grid">
          <div className="benefit-card">
            <h3>Local Expertise</h3>
            <p>Our guides know every secret spot for Aurora viewing and wildlife encounters.</p>
          </div>
          <div className="benefit-card">
            <h3>Small Groups</h3>
            <p>Maximum 8 guests per tour for personalized attention and minimal environmental impact.</p>
          </div>
          <div className="benefit-card">
            <h3>Eco-Conscious</h3>
            <p>We partner with sustainable operators and follow Leave No Trace principles.</p>
          </div>
        </div>
      </section>

      <section className="testimonial-section">
        <h2>What Our Guests Say</h2>
        <blockquote>
          "The Northern Lights tour with Kiruna Tours exceeded all expectations! Our guide knew exactly where to go despite cloudy forecasts. Saw incredible Aurora displays three nights in a row."
          <cite>- Sarah K., February 2024</cite>
        </blockquote>
      </section>

      <section className="faq-preview">
        <h2>Common Questions</h2>
        <div className="faq-items">
          <div className="faq-item">
            <h3>When is the best time to see Northern Lights?</h3>
            <p>September through April, with peak visibility from December to March.</p>
          </div>
          <div className="faq-item">
            <h3>How cold does it get in winter?</h3>
            <p>Typically -10°C to -25°C, but we provide thermal clothing for all tours.</p>
          </div>
        </div>
        <Link to="/faq/" className="cta-button">View Full FAQ</Link>
      </section>

      <Suspense fallback={null}>
        {isAIOpen && <AIWidget visible={true} onClose={() => setIsAIOpen(false)} />}
      </Suspense>
    
<section style={{ marginTop: "40px" }}>
  <h2>Northern Lights Forecast in Kiruna</h2>
  <p>
    Planning to see Aurora tonight? Read our complete{" "}
    <Link to="/travel-guide/northern-lights-forecast-explained/">
      Northern Lights Forecast Guide
    </Link>{" "}
    with KP index explanation, best months and viewing locations.
  </p>
</section>


<section style={{marginTop:"40px"}}>
  <h2>Popular Kiruna Travel Guides</h2>
  <ul>
    <li><Link to="/travel-guide/kiruna-winter-itinerary/">Top Things to Do in Kiruna</Link></li>
    <li><Link to="/travel-guide/swedish-lapland-winter-travel-guide/">Kiruna Travel Guide</Link></li>
    <li><Link to="/travel-guide/snowmobile-tours-in-kiruna-guide/">Snowmobile Tours in Kiruna</Link></li>
    <li><Link to="/travel-guide/kiruna-sami-reindeer-experiences/">Reindeer Experience in Kiruna</Link></li>
    <li><Link to="/travel-guide/kiruna-aurora-river-camp-unique-stays/">Glass Igloo Stay in Kiruna</Link></li>
    <li><a href="/tours/day-trip-to-abisko/">Day Trip to Abisko</a></li>
    <li><a href="/tours/visit-the-world-famous-icehotel/">Visit the ICEHOTEL</a></li>
    <li><a href="/tours/ice-climbing-tour-in-abisko-beginner-friendly/">Ice Climbing in Abisko</a></li>
  </ul>
</section>

</main>
  );
}